import { useState, useEffect, useCallback, type Dispatch, type SetStateAction } from 'react';

/**
 * A drop-in replacement for useState that persists state to sessionStorage.
 * Survives back-navigation within the same tab but clears when the tab is closed.
 */
export function usePersistedFilterState<T>(
  key: string,
  defaultValue: T
): [T, Dispatch<SetStateAction<T>>] {
  const [state, setStateInternal] = useState<T>(() => {
    try {
      const stored = sessionStorage.getItem(key);
      if (stored) {
        return JSON.parse(stored) as T;
      }
    } catch {
      // Ignore parse errors, fall through to default
    }
    return defaultValue;
  });

  // Sync to sessionStorage whenever state changes
  useEffect(() => {
    try {
      const serialized = JSON.stringify(state);
      // Clear key if value equals default (empty array)
      if (Array.isArray(state) && (state as unknown[]).length === 0) {
        sessionStorage.removeItem(key);
      } else {
        sessionStorage.setItem(key, serialized);
      }
    } catch {
      // Ignore write errors (e.g. storage full)
    }
  }, [key, state]);

  return [state, setStateInternal];
}
